///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Device location tracking behind the "my location" camera button: a
/// navigator.geolocation watch plus (on touch devices) the compass
/// heading from DeviceOrientationEvent, published to dialog.userLocation
/// for the blue marker (EarthActivity/utils/UserLocationMarker.ts) and
/// the follow / heading camera modes (camera-director/FollowBehavior.ts).
///
/// Everything stays on the device: fixes go to redux (not persisted) and
/// nowhere else. The watch pauses while the page is hidden and resumes
/// when it is shown again.
///

import { clearUserLocation, updateUserLocation, type UserLocationFix } from "@/redux/dialogSlice"
import type { AppDispatch } from "@/redux/store"

/** Minimum interval between heading publishes (the marker eases between them) */
const cHeadingPublishMs = 100
/** Heading change below which a sample is not published (degrees) */
const cHeadingMinDeltaDeg = 1
/** Course over ground stands in for the compass only while actually moving */
const cCourseMinSpeedMps = 1.5

/** iOS exposes the permission gate as a static on the event class */
interface OrientationEventWithPermission {
	requestPermission?: () => Promise<"granted" | "denied">
}

/** Safari's true-north compass field on deviceorientation events */
interface WebkitOrientationEvent extends DeviceOrientationEvent {
	webkitCompassHeading?: number
}

class UserLocationService {
	private dispatch: AppDispatch | null = null
	private watchId: number | null = null
	private orientationEvent: "deviceorientationabsolute" | "deviceorientation" | null = null
	private lastHeadingDeg: number | null = null
	private lastHeadingPublish = 0
	private compassSeen = false
	private hadFix = false
	private active = false

	/** True on devices where a compass heading is plausible (phones, tablets) */
	public get headingSupported(): boolean {
		return typeof window !== "undefined"
			&& "DeviceOrientationEvent" in window
			&& window.matchMedia("(pointer: coarse)").matches
	}

	public get running(): boolean {
		return this.active
	}

	/**
	 * Start (or restart) the location watch. Call from a user gesture: on
	 * iOS the compass permission prompt only opens from one, and this asks
	 * for it synchronously before the geolocation watch starts.
	 */
	public start(dispatch: AppDispatch): void {
		this.dispatch = dispatch
		if (!navigator.geolocation) {
			dispatch(updateUserLocation({ status: "unavailable", headingSupported: false }))
			return
		}
		const headingSupported = this.headingSupported
		this.active = true
		dispatch(updateUserLocation({ status: this.hadFix ? "tracking" : "locating", headingSupported }))

		if (headingSupported && !this.orientationEvent) {
			this.startHeading()
		}
		this.startWatch()
		document.addEventListener("visibilitychange", this.onVisibilityChange)
	}

	/** Stop watching and drop the marker */
	public stop(): void {
		this.active = false
		this.stopWatch()
		this.stopHeading()
		document.removeEventListener("visibilitychange", this.onVisibilityChange)
		this.hadFix = false
		this.dispatch?.(clearUserLocation())
	}

	private startWatch(): void {
		if (this.watchId !== null || !navigator.geolocation) return
		this.watchId = navigator.geolocation.watchPosition(
			(pos) => this.onFix(pos),
			(err) => this.onError(err),
			{ enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
		)
	}

	private stopWatch(): void {
		if (this.watchId !== null && navigator.geolocation) {
			navigator.geolocation.clearWatch(this.watchId)
		}
		this.watchId = null
	}

	private onFix(pos: GeolocationPosition): void {
		const c = pos.coords
		const fix: UserLocationFix = {
			lat: c.latitude,
			lon: c.longitude,
			accuracyM: Number.isFinite(c.accuracy) ? c.accuracy : 1000,
			altitudeM: c.altitude,
			speedMps: c.speed,
			courseDeg: c.heading !== null && Number.isFinite(c.heading) ? c.heading : null,
			timestamp: pos.timestamp,
		}
		this.hadFix = true
		this.dispatch?.(updateUserLocation({ status: "tracking", fix }))

		// No compass events yet (desktop, or permission declined): the GPS
		// course gives a heading while moving, as the maps apps do in a car
		if (!this.compassSeen && fix.courseDeg !== null && (fix.speedMps ?? 0) >= cCourseMinSpeedMps) {
			this.publishHeading(fix.courseDeg, true)
		}
	}

	private onError(err: GeolocationPositionError): void {
		console.warn("[userLocation] geolocation error:", err.message)
		if (err.code === err.PERMISSION_DENIED) {
			this.active = false
			this.stopWatch()
			this.stopHeading()
			this.dispatch?.(updateUserLocation({ status: "denied", fix: null }))
		} else if (!this.hadFix) {
			// Timeout / position unavailable before any fix: give up so the
			// button stops pulsing; a later fix (the watch keeps running on
			// a timeout) flips the status back to tracking. Errors after a
			// fix leave the marker where it was, as the maps apps do
			this.dispatch?.(updateUserLocation({ status: "unavailable" }))
		}
	}

	// ========== Compass heading ==========

	private startHeading(): void {
		const ctor = DeviceOrientationEvent as unknown as OrientationEventWithPermission
		if (typeof ctor.requestPermission === "function") {
			// iOS 13+: must be invoked from the user gesture that called start()
			ctor.requestPermission().then((result) => {
				if (result === "granted") {
					this.listenOrientation()
				} else {
					// No compass this session: the button skips heading mode
					this.dispatch?.(updateUserLocation({ headingSupported: false }))
				}
			}).catch(() => {
				this.dispatch?.(updateUserLocation({ headingSupported: false }))
			})
		} else {
			this.listenOrientation()
		}
	}

	private listenOrientation(): void {
		if (this.orientationEvent) return
		// Android Chrome: the absolute variant is referenced to north; the
		// plain event there is relative to the device's starting pose
		this.orientationEvent = "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation"
		window.addEventListener(this.orientationEvent, this.onOrientation as EventListener, { passive: true })
	}

	private stopHeading(): void {
		if (this.orientationEvent) {
			window.removeEventListener(this.orientationEvent, this.onOrientation as EventListener)
			this.orientationEvent = null
		}
		this.compassSeen = false
		this.lastHeadingDeg = null
	}

	private readonly onOrientation = (event: WebkitOrientationEvent): void => {
		let heading: number | null = null
		if (typeof event.webkitCompassHeading === "number" && Number.isFinite(event.webkitCompassHeading)) {
			heading = event.webkitCompassHeading
		} else if (event.alpha !== null && (event.absolute || this.orientationEvent === "deviceorientationabsolute")) {
			// alpha turns counter-clockwise from north; fold in the screen
			// rotation so a landscape phone still points the cone forward
			const screenAngle = (screen.orientation?.angle ?? 0)
			heading = (360 - event.alpha + screenAngle) % 360
		}
		if (heading === null) return
		this.compassSeen = true
		this.publishHeading(heading, false)
	}

	private publishHeading(heading: number, fromCourse: boolean): void {
		const now = performance.now()
		if (this.lastHeadingDeg !== null) {
			const delta = Math.abs(((heading - this.lastHeadingDeg + 540) % 360) - 180)
			if (delta < cHeadingMinDeltaDeg) return
			if (!fromCourse && now - this.lastHeadingPublish < cHeadingPublishMs) return
		}
		this.lastHeadingDeg = heading
		this.lastHeadingPublish = now
		this.dispatch?.(updateUserLocation({ headingDeg: heading }))
	}

	// ========== Page visibility ==========

	private readonly onVisibilityChange = (): void => {
		if (!this.active) return
		if (document.visibilityState === "hidden") {
			this.stopWatch()
		} else {
			this.startWatch()
		}
	}
}

export const userLocationService = new UserLocationService()
